import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../contexts/ContextoWorkspace';
import { centrosCostoService } from '../services/api/centrosCosto.service';

export interface CentroCosto {
  id: string;
  codigo: string;
  nombre: string;
}

export interface CentroCostoInput {
  codigo: string;
  nombre: string;
}

interface UseCentrosCostoReturn {
  centrosCosto: CentroCosto[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  crearCentroCosto: (input: CentroCostoInput) => Promise<CentroCosto>;
}

export function useCentrosCosto(): UseCentrosCostoReturn {
  const { empresaId } = useWorkspace();
  const [centrosCosto, setCentrosCosto] = useState<CentroCosto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esCargandoRef = useRef(false);

  const scopeReady = empresaId != null && empresaId.trim() !== '';

  const refresh = useCallback(async () => {
    if (!scopeReady || !empresaId) {
      setCentrosCosto([]);
      return;
    }
    if (esCargandoRef.current) return;
    esCargandoRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const response = await centrosCostoService.getAll(empresaId);
      const mapped: CentroCosto[] = (response.data || []).map((item) => ({
        id: item.id,
        codigo: item.codigo,
        nombre: item.nombre,
      }));
      setCentrosCosto(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los centros de costo');
    } finally {
      setLoading(false);
      esCargandoRef.current = false;
    }
  }, [empresaId, scopeReady]);

  useEffect(() => {
    void refresh();
  }, [empresaId, scopeReady]);

  const crearCentroCosto = useCallback(async (input: CentroCostoInput): Promise<CentroCosto> => {
    if (!scopeReady || !empresaId) throw new Error('Selecciona una empresa para registrar centros de costo');
    setLoading(true);
    setError(null);
    try {
      const response = await centrosCostoService.create({
        empresaId,
        codigo: input.codigo,
        nombre: input.nombre,
      });
      const created: CentroCosto = {
        id: response.data.id,
        codigo: response.data.codigo,
        nombre: response.data.nombre,
      };
      setCentrosCosto((prev) => [...prev, created]);
      return created;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear el centro de costo';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [empresaId, scopeReady]);

  return { centrosCosto, loading, error, refresh, crearCentroCosto };
}
